import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { fetchSpaceGroups } from '../../api/lib/space-groups';
import { spaceGroupsKeys } from '../../api/query-keys/space-groups';
import { SpaceGroupsActionsTableMenu } from '../molecules/space-groups-actions-table-menu';
import { SpaceGroupsActionsSlotPayload } from '../molecules/space-groups-table-row';
import { AssignEmployeeGroupsToSpaceGroupDialog } from '../organisms/dialogs/assign-employee-groups-to-space-group-dialog';
import { CreateSpaceGroupDialog } from '../organisms/dialogs/create-space-group-dialog/create-space-group-dialog';
import { DeleteSpaceGroupDialog } from '../organisms/dialogs/delete-space-group-dialog';
import { UpdateSpaceGroupNameDialog } from '../organisms/dialogs/update-space-group-name-dialog/update-space-group-name-dialog';
import { SpaceGroupsTable } from '../organisms/space-groups-table';

const SpaceGroups = () => {
  const { t } = useTranslation(['spaceGroups', 'common']);

  const { directoryToken } = useUserStore();

  const { query, perPage, page } = useSearch({
    from: '/_layout/tenant/spaces/_layout/groups',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'name',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeSort,
  } = useMeilisearchWithUrlParams(
    fetchSpaceGroups,
    spaceGroupsKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const [
    isOpenCreateSpaceGroupDialog,
    handleOpenCreateSpaceGroupDialog,
    handleCloseCreateSpaceGroupDialog,
  ] = useDialog();

  const [
    isOpenUpdateSpaceGroupNameDialog,
    handleOpenUpdateSpaceGroupNameDialog,
    handleCloseUpdateSpaceGroupNameDialog,
  ] = useDialog();

  const [
    isOpenDeleteSpaceGroupDialog,
    handleOpenDeleteSpaceGroupDialog,
    handleCloseDeleteSpaceGroupDialog,
  ] = useDialog();

  const [
    isOpenAssignEmployeeGroupsToSpaceGroupDialog,
    handleOpenAssignEmployeeGroupsToSpaceGroupDialog,
    handleCloseAssignEmployeeGroupsToSpaceGroupDialog,
  ] = useDialog();

  const [selectedSpaceGroup, setSelectedSpaceGroup] =
    useState<SpaceGroupsActionsSlotPayload | null>(null);

  const actionsSlot = useCallback(
    ({ token, name, employeesGroupsTokens }: SpaceGroupsActionsSlotPayload) => (
      <SpaceGroupsActionsTableMenu
        onEditName={() => {
          setSelectedSpaceGroup({ token, name, employeesGroupsTokens });
          handleOpenUpdateSpaceGroupNameDialog();
        }}
        onManageEmployeesGroups={() => {
          setSelectedSpaceGroup({ token, name, employeesGroupsTokens });
          handleOpenAssignEmployeeGroupsToSpaceGroupDialog();
        }}
        onDelete={() => {
          setSelectedSpaceGroup({ token, name, employeesGroupsTokens });
          handleOpenDeleteSpaceGroupDialog();
        }}
      />
    ),
    [],
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  return (
    <>
      <PageHeader
        title={t('spaceGroups:common.title')}
        extras={
          <Can I="create" a="SpaceGroups" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={handleOpenCreateSpaceGroupDialog}
                    color="primary"
                    disabled={!allowed}
                    startIcon={<Plus fontSize="small" />}
                    variant="contained"
                    id="create_space_group_button"
                    size="large">
                    {t('spaceGroups:common.addNewGroup')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        }
      />

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />
        </Box>

        <SpaceGroupsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <CreateSpaceGroupDialog
        open={isOpenCreateSpaceGroupDialog}
        onCancel={handleCloseCreateSpaceGroupDialog}
      />

      <UpdateSpaceGroupNameDialog
        open={isOpenUpdateSpaceGroupNameDialog}
        onCancel={() => {
          handleCloseUpdateSpaceGroupNameDialog();
          setSelectedSpaceGroup(null);
        }}
        selectedSpaceGroup={selectedSpaceGroup}
      />

      <DeleteSpaceGroupDialog
        open={isOpenDeleteSpaceGroupDialog}
        onCancel={() => {
          handleCloseDeleteSpaceGroupDialog();
          setSelectedSpaceGroup(null);
        }}
        selectedSpaceGroup={selectedSpaceGroup}
      />

      <AssignEmployeeGroupsToSpaceGroupDialog
        open={isOpenAssignEmployeeGroupsToSpaceGroupDialog}
        onCancel={() => {
          handleCloseAssignEmployeeGroupsToSpaceGroupDialog();
          setSelectedSpaceGroup(null);
        }}
        selectedSpaceGroup={selectedSpaceGroup}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'SpaceGroups'),
  SpaceGroups,
);
